import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import {
  EntityDrug,
  IConfiguration,
  ICycleSchema,
  IDrugInfo,
  IDrugsHistoric,
  IEntitylink,
  IKnowledges,
  INotification,
  KNOW_DOC_CATEGORY,
  NOTIFICATION_STATUS,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TimingData } from "src/app/models/timingData.model";
import { DrugsService } from "src/app/providers/drugs.service";
import { DrugSchemaService } from "src/app/providers/drugSchema.service";
import { PatientService } from "src/app/providers/patient.service";
import { SessionService } from "src/app/providers/session.service";
import { AddDrugIntakeDialogComponent } from "../add-drug-intake-dialog/add-drug-intake-dialog.component";

@Component({
  selector: "app-drug-expansion-panel",
  templateUrl: "./drug-expansion-panel.component.html",
  styleUrls: ["./drug-expansion-panel.component.scss"],
})
export class DrugExpansionPanelComponent implements OnInit {
  @Input() data: IEntitylink[];
  @Input() dataDrugs: IEntitylink[];
  @Input() loading: boolean;
  @Input() knowledgesLoaded: boolean;
  @Input() drugsKnowledges: IKnowledges[];
  @Input() drugsInfo: IDrugInfo[];
  @Input() historic: IDrugsHistoric[];
  @Input() intakeDrugs: INotification[];
  @Input() patientId: string;

  @Output() triggerShowKnowledges = new EventEmitter<{ drug: EntityDrug; docCategory: KNOW_DOC_CATEGORY }>();
  @Output() triggerUpdateDrug = new EventEmitter<IEntitylink>();
  @Output() triggerDeleteDrug = new EventEmitter<IEntitylink>();
  @Output() triggerShowHistoricLocal = new EventEmitter<IEntitylink>();
  @Output() triggerOnPauseCycle = new EventEmitter<IEntitylink>();
  @Output() triggerShowMore = new EventEmitter<IEntitylink>();

  public toggleExpansion = [];
  public KNOW_DOC_CATEGORY = KNOW_DOC_CATEGORY;
  public drugNotifications: { [key: string]: INotification[] } = {};
  public NOTIFICATION_STATUS = NOTIFICATION_STATUS;
  public patientConfig: IConfiguration;

  private onDestroy$ = new Subject<void>();

  constructor(
    private drugsService: DrugsService,
    private sessionService: SessionService,
    private drugSchemaService: DrugSchemaService,
    private dialog: MatDialog,
    private patientService: PatientService,
    private translateService: TranslateService
  ) {
    this.sessionService.refreshDrugsData.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.loadIntakes();
    });
  }

  ngOnInit(): void {
    this.patientService
      .getPatientConfiguration(this.patientId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        this.patientConfig = config;
      });
    this.loadIntakes();
  }

  public isInfinite(date: string): boolean {
    return this.drugsService.isInfinite(date);
  }

  public showKnowledges(drug: EntityDrug, docCategory: KNOW_DOC_CATEGORY): void {
    this.triggerShowKnowledges.emit({ drug, docCategory });
  }

  public onURL(drug: EntityDrug): void {
    const userLang = this.sessionService.userLang;
    const infos: IDrugInfo = this.drugsInfo.find((d) => d?.cnk === drug.reference);
    if (infos) {
      const url = infos[userLang] ? infos[userLang].url : infos.fr.url;
      window.open(url, "_blank");
    }
  }

  public updateDrug(drug: IEntitylink): void {
    this.triggerUpdateDrug.emit(drug);
  }

  public deleteDrug(drug: IEntitylink): void {
    this.triggerDeleteDrug.emit(drug);
  }

  public showHistoricLocal(drug: IEntitylink): void {
    this.triggerShowHistoricLocal.emit(drug);
  }

  public isCycleInPause(cycle: ICycleSchema): boolean {
    return this.drugSchemaService.isCycleInPause(cycle);
  }

  public OnPauseCycle(element: IEntitylink): void {
    this.triggerOnPauseCycle.emit(element);
  }

  public getFormatDate(d: Date): string {
    return this.drugsService.getFormatDate(d);
  }

  public showMore(element: IEntitylink): void {
    this.triggerShowMore.emit(element);
  }

  private loadIntakes() {
    const today = moment().startOf("day");
    const yesterday = moment().subtract(1, "day").startOf("day");

    this.data.forEach((drug) => {
      const entityId = drug._id;
      const drugHistoric = this.historic.filter((v) => v.entityId === entityId).reverse();
      const supposedIntakes = DrugsService.getSupposedIntakes(drug.entityData, drugHistoric);
      const cibledNotif = DrugsService.getCibledNotification(entityId, this.intakeDrugs).reverse();
      const combinedNotifs = DrugsService.combineSupposedAndRealIntakes(supposedIntakes, cibledNotif);

      this.drugNotifications[drug._id] = combinedNotifs.filter((notif) => {
        const notifDate = moment(notif.time).startOf("day");
        return notifDate.isSame(today) || notifDate.isSame(yesterday);
      });
    });
  }

  public onAddScheduledIntake(drug: IEntitylink, notif: INotification): void {
    const time = TimingData.getMomentHour(notif.moment, this.patientConfig.settings.notificationSettings, notif.time);
    const dialogRef = this.dialog.open(AddDrugIntakeDialogComponent, {
      data: {
        patientId: this.patientId,
        drug: drug,
        time: time ? time : notif.time,
        moment: notif.moment,
        quantity: notif.quantity,
        allNotifications: this.drugNotifications[drug._id],
        isScheduled: true,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result?.length > 0) {
          this.sessionService.needRefreshDrugsData();
        }
      });
  }

  public onAddUnscheduledIntake(drug: IEntitylink): void {
    const dialogRef = this.dialog.open(AddDrugIntakeDialogComponent, {
      data: {
        patientId: this.patientId,
        drug: drug,
        allNotifications: this.drugNotifications[drug._id],
        isScheduled: false,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result?.length > 0) {
          this.sessionService.needRefreshDrugsData();
        }
      });
  }

  public onEditIntake(drug: IEntitylink, notif: INotification): void {
    const dialogRef = this.dialog.open(AddDrugIntakeDialogComponent, {
      data: {
        patientId: this.patientId,
        drug: drug,
        time: notif.time,
        moment: notif.moment,
        quantity: notif.quantity,
        allNotifications: this.drugNotifications[drug._id],
        isScheduled: notif.unscheduledIntake ? false : true,
        status: notif.status,
        comment: notif.comment,
        administrationTime: notif.administrationTime,
        quantityTaken: notif.quantityTaken,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result?.length > 0) {
          this.sessionService.needRefreshDrugsData();
        }
      });
  }

  public onDeleteIntake(drug: IEntitylink, notif: INotification): void {
    const message = this.translateService.instant("drugIntake.confirmDeleteSupposedIntake");

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message,
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm) => {
        if (confirm) {
          this.drugsService
            .cancelScheduledDrugIntake(this.patientId, drug, notif.time, notif.moment)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
              if (result) {
                this.sessionService.needRefreshDrugsData();
              }
            });
        }
      });
  }
}
